import { useState } from 'react';
import {
  ArrowRight, CheckCircle2, Sparkles, Cloud, GraduationCap, ShieldCheck, Code2, Brain, Bot, LayoutGrid,
  BellRing, Lock, Star, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { joinAwsWaitlist } from '@/lib/api';

// AWS Certification roadmap — 7 programs in development (no live modules yet).
const AWS_CERTS = [
  {
    code: 'aws-clf',
    name: 'AWS Cloud Practitioner',
    fullName: 'AWS Certified Cloud Practitioner',
    level: 'Foundational',
    levelColor: 'bg-emerald-100 text-emerald-700',
    accent: 'from-emerald-500 to-teal-600',
    icon: Cloud,
    blurb: 'The on-ramp to AWS. Core cloud concepts, services, security, billing, and architecture basics — no prior tech background required.',
    status: 'Coming Q2',
    eta: 'Apr 2026',
    skills: ['Cloud concepts', 'Core AWS services', 'Security & billing', 'Architectural basics'],
  },
  {
    code: 'aws-aip',
    name: 'AWS AI Practitioner',
    fullName: 'AWS Certified AI Practitioner',
    level: 'Foundational',
    levelColor: 'bg-emerald-100 text-emerald-700',
    accent: 'from-amber-500 to-orange-600',
    icon: Sparkles,
    blurb: 'AWS\'s newest foundational cert. AI/ML fundamentals, generative AI on AWS (Bedrock, SageMaker), responsible AI, and prompt engineering.',
    status: 'Coming Q2',
    eta: 'May 2026',
    skills: ['AI/ML foundations', 'Generative AI on AWS', 'Bedrock & SageMaker', 'Responsible AI'],
  },
  {
    code: 'aws-saa',
    name: 'AWS Solutions Architect – Associate',
    fullName: 'AWS Certified Solutions Architect – Associate',
    level: 'Associate',
    levelColor: 'bg-sky-100 text-sky-700',
    accent: 'from-sky-500 to-blue-600',
    icon: LayoutGrid,
    blurb: 'The flagship AWS cert. Design resilient, high-performing, secure, cost-optimized architectures on AWS for real-world workloads.',
    status: 'Coming Q3',
    eta: 'Jul 2026',
    skills: ['Resilient architectures', 'Cost-optimized design', 'Security best practices', 'High availability'],
  },
  {
    code: 'aws-dva',
    name: 'AWS Developer – Associate',
    fullName: 'AWS Certified Developer – Associate',
    level: 'Associate',
    levelColor: 'bg-sky-100 text-sky-700',
    accent: 'from-indigo-500 to-purple-600',
    icon: Code2,
    blurb: 'Build, deploy, and debug cloud applications. Lambda, DynamoDB, API Gateway, CodePipeline, X-Ray, and serverless patterns.',
    status: 'Coming Q3',
    eta: 'Aug 2026',
    skills: ['Serverless dev', 'Lambda & API Gateway', 'DynamoDB patterns', 'CI/CD on AWS'],
  },
  {
    code: 'aws-mla',
    name: 'AWS ML Engineer – Associate',
    fullName: 'AWS Certified Machine Learning Engineer – Associate',
    level: 'Associate',
    levelColor: 'bg-sky-100 text-sky-700',
    accent: 'from-violet-500 to-fuchsia-600',
    icon: Brain,
    blurb: 'New ML-focused associate cert. Data engineering for ML, model dev with SageMaker, deployment, monitoring, and MLOps on AWS.',
    status: 'Coming Q3',
    eta: 'Sep 2026',
    skills: ['Data prep for ML', 'SageMaker model dev', 'ML deployment', 'MLOps on AWS'],
  },
  {
    code: 'aws-scs',
    name: 'AWS Security – Specialty',
    fullName: 'AWS Certified Security – Specialty',
    level: 'Specialty',
    levelColor: 'bg-rose-100 text-rose-700',
    accent: 'from-rose-500 to-pink-600',
    icon: ShieldCheck,
    blurb: 'Deep-dive AWS security. Incident response, logging & monitoring, IAM at scale, data protection, infrastructure hardening.',
    status: 'Coming Q4',
    eta: 'Oct 2026',
    skills: ['Incident response', 'IAM at scale', 'Data protection', 'Threat detection'],
  },
  {
    code: 'aws-gaip',
    name: 'AWS Generative AI Developer – Professional',
    fullName: 'AWS Certified Generative AI Developer – Professional',
    level: 'Professional',
    levelColor: 'bg-amber-100 text-amber-700',
    accent: 'from-orange-500 to-red-600',
    icon: Bot,
    blurb: 'AWS\'s most advanced GenAI cert. Architect production-grade generative AI on AWS — RAG, agents, fine-tuning, evaluation, safety.',
    status: 'Coming Q4',
    eta: 'Dec 2026',
    skills: ['Production GenAI architecture', 'Agents & RAG on AWS', 'Fine-tuning & evaluation', 'AI safety at scale'],
  },
];

export default function AWSTrackPage() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [certInterest, setCertInterest] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await joinAwsWaitlist({
        email: email.trim(),
        name: name.trim() || null,
        cert_interest: certInterest || null,
      });
      toast.success(data.message || 'You\'re on the waitlist!');
      setJoined(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail[0]?.msg || 'Invalid input' : (detail || 'Could not join waitlist. Try again.');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="aws-track-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-amber-950 text-white pt-28 pb-20">
        <div
          className="absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              'radial-gradient(circle at 15% 25%, rgba(251,191,36,0.4) 0px, transparent 50%), radial-gradient(circle at 85% 70%, rgba(249,115,22,0.35) 0px, transparent 50%)',
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Badge className="bg-white/10 text-white border-white/20 mb-6 hover:bg-white/15">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> AWS Track · 7 certs in development
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight max-w-4xl">
            Master the <span className="text-amber-300">AWS cloud</span> — from
            <span className="text-orange-300"> first principles</span> to
            <span className="text-amber-300"> production GenAI</span>.
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl">
            The complete AWS career ladder is coming to LearnQuanta: Foundational, Associate, Specialty, and Professional —
            with the same AI-coach + simulation + paywall experience you love on the Microsoft and Google tracks.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#track">
              <Button
                size="lg"
                className="bg-amber-400 hover:bg-amber-300 text-slate-950 font-semibold"
                data-testid="aws-hero-view-track-btn"
              >
                See the 7-cert AWS roadmap <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <a href="#waitlist">
              <Button
                size="lg"
                variant="outline"
                className="border-white/30 bg-white/5 text-white hover:bg-white/10"
                data-testid="aws-hero-waitlist-btn"
              >
                <BellRing className="w-4 h-4 mr-2" /> Join the waitlist
              </Button>
            </a>
          </div>

          <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-3xl">
            {[
              { v: '7', l: 'AWS certs planned' },
              { v: '2026', l: 'Rolling launch' },
              { v: '4', l: 'Tiers (Found → Pro)' },
              { v: 'GPT-4o', l: 'AI Coach 1:1' },
            ].map((s) => (
              <div key={s.l} className="rounded-xl bg-white/5 border border-white/10 p-4 backdrop-blur">
                <div className="text-2xl font-bold">{s.v}</div>
                <div className="text-xs text-slate-300 uppercase tracking-wide">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CERT GRID */}
      <section id="track" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-12">
            <Badge variant="outline" className="mb-3">
              <GraduationCap className="w-3.5 h-3.5 mr-1" /> In development · 2026
            </Badge>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
              The complete AWS certification ladder
            </h2>
            <p className="mt-3 text-slate-600">
              From your first cloud cert to production-grade GenAI engineering on AWS. Vote with your waitlist signups
              which one we ship first — Foundational certs typically lead the rollout.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {AWS_CERTS.map((c) => {
              const Icon = c.icon;
              return (
                <Card
                  key={c.code}
                  className="border-slate-200 hover:shadow-xl transition-shadow"
                  data-testid={`aws-track-cert-card-${c.code}`}
                >
                  <div className={`h-1.5 bg-gradient-to-r ${c.accent} rounded-t-lg`} />
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${c.accent} flex items-center justify-center`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <Badge className={c.levelColor}>{c.level}</Badge>
                    </div>
                    <CardTitle className="mt-4 text-lg">{c.name}</CardTitle>
                    <CardDescription className="text-xs">{c.fullName}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-600 mb-4">{c.blurb}</p>
                    <ul className="space-y-1.5 mb-5">
                      {c.skills.map((s) => (
                        <li key={s} className="flex items-start gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" /> {s}
                        </li>
                      ))}
                    </ul>
                    <div className="flex items-center justify-between mb-4 text-xs text-slate-500">
                      <span><Star className="w-3 h-3 inline mr-1" />{c.status}</span>
                      <span>ETA: {c.eta}</span>
                    </div>
                    <Button
                      disabled
                      className="w-full bg-slate-100 text-slate-500 hover:bg-slate-100 cursor-not-allowed"
                      data-testid={`aws-track-cert-soon-${c.code}`}
                    >
                      <Lock className="w-4 h-4 mr-2" /> Coming soon
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* WAITLIST CTA */}
      <section id="waitlist" className="py-20 bg-gradient-to-br from-amber-500 to-orange-600 text-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <BellRing className="w-12 h-12 mx-auto mb-4 opacity-90" />
          <h2 className="text-3xl sm:text-4xl font-bold">Be first when AWS modules go live</h2>
          <p className="mt-4 text-amber-50 text-lg">
            Drop your details and we&apos;ll email you the moment a new AWS cert opens — plus an exclusive launch discount for waitlist members.
          </p>

          {joined ? (
            <div className="mt-8 p-6 rounded-xl bg-white/15 border border-white/30 backdrop-blur" data-testid="aws-waitlist-success">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3" />
              <p className="text-lg font-semibold">You&apos;re on the list!</p>
              <p className="text-sm text-amber-50 mt-1">Check your inbox for confirmation — we&apos;ll be in touch as each AWS cert launches.</p>
            </div>
          ) : (
            <form onSubmit={handleWaitlistSubmit} className="mt-8 grid gap-3 text-left" data-testid="aws-waitlist-form">
              <div className="grid sm:grid-cols-2 gap-3">
                <Input
                  type="text"
                  placeholder="Your name (optional)"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="bg-white/95 border-white/40 text-slate-900 placeholder:text-slate-500"
                  data-testid="aws-waitlist-name-input"
                />
                <Input
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-white/95 border-white/40 text-slate-900 placeholder:text-slate-500"
                  data-testid="aws-waitlist-email-input"
                />
              </div>
              <select
                value={certInterest}
                onChange={(e) => setCertInterest(e.target.value)}
                className="h-10 rounded-md bg-white/95 border-white/40 text-slate-900 px-3 text-sm w-full"
                data-testid="aws-waitlist-cert-select"
              >
                <option value="">Which AWS cert do you want first? (optional)</option>
                {AWS_CERTS.map((c) => (
                  <option key={c.code} value={c.fullName}>{c.fullName}</option>
                ))}
              </select>
              <Button
                type="submit"
                size="lg"
                disabled={submitting}
                className="bg-white text-orange-700 hover:bg-orange-50 font-semibold mt-2"
                data-testid="aws-waitlist-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Joining...</>
                ) : (
                  <>Join the AWS waitlist <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
              <p className="text-xs text-amber-50/80 mt-1 text-center">
                No spam, ever. We&apos;ll only email you about AWS cert launches and waitlist discounts.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
